<script setup lang="ts">
  import GeneralSettingsTab from '@/components/dialogs/partials/GeneralSettingsTab.vue'
  import UpdatePasswordForm from '@/components/profile/UpdatePasswordForm.vue'

  import { useI18n } from 'vue-i18n'
  import { useDisplay } from 'vuetify'
  import { useSettingsDialog } from '@/composables/useSettingsDialog.ts'

  const { t } = useI18n()
  const { mobile } = useDisplay()
  const { model, selectedTab, hide } = useSettingsDialog()
</script>

<template>
  <v-dialog
    v-model="model"
    :fullscreen="mobile"
    max-width="690"
    :min-height="mobile ? 'auto' : 790"
    scrollable
  >
    <v-card class="bg-login-bg d-flex flex-column flex-lg-row" :rounded="mobile ? '0' : 'xl'">
      <div class="d-flex justify-end d-lg-none pa-4">
        <v-btn
          icon="mdi-close"
          size="small"
          variant="text"
          @click="hide"
        />
      </div>

      <!-- Tabs Section -->
      <div
        class="flex-sm-shrink-0 w-100 w-lg-33 pt-lg-5 px-lg-5 pb-lg-10"
        :class="{'border-b-sm border-grey-800 border-opacity-100' : mobile}"
      >
        <div class="mb-6 text-subtitle-1 text-grey-400 font-weight-bold d-none d-lg-block">
          {{ t('account_settings') }}
        </div>
        <v-tabs
          v-model="selectedTab"
          class="settings-tabs"
          :direction="mobile ? 'horizontal' : 'vertical'"
          hide-slider
        >
          <v-tab
            class="pa-3 text-body-2 rounded-md mb-1"
            :class="{
              'justify-center mx-1': mobile,
              'justify-start': !mobile,
            }"
            height="44"
            selected-class="font-weight-bold bg-grey-800"
            value="personal_info"
          >
            {{ t('personal_info') }}
          </v-tab>
          <v-tab
            class="pa-3 text-body-2 rounded-md mb-1"
            :class="{
              'justify-center mx-1': mobile,
              'justify-start': !mobile,
            }"
            height="44"
            selected-class="font-weight-bold bg-grey-800"
            value="general_settings"
          >
            {{ t('general_settings') }}
          </v-tab>
          <v-tab
            class="pa-3 text-body-2 rounded-md mb-1"
            :class="{
              'justify-center mx-1': mobile,
              'justify-start': !mobile,
            }"
            height="44"
            selected-class="font-weight-bold bg-grey-800"
            value="my_subscriptions"
          >
            {{ t('my_subscriptions') }}
          </v-tab>
          <v-tab
            class="pa-3 text-body-2 rounded-md mb-1"
            :class="{
              'justify-center mx-1': mobile,
              'justify-start': !mobile,
            }"
            height="44"
            selected-class="font-weight-bold bg-grey-800"
            value="change_password"
          >
            {{ t('change_password') }}
          </v-tab>
        </v-tabs>
      </div>

      <!-- Content Section -->
      <div class="flex-grow-1 border-s-sm border-grey-800 border-opacity-100 pt-5 ps-5 pe-8 pb-10 overflow-y-auto">
        <v-tabs-window v-model="selectedTab">
          <div class="d-none justify-end mb-6 d-lg-flex">
            <v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              @click="hide"
            />
          </div>
          <v-tabs-window-item value="personal_info">
            <personal-info-form />
          </v-tabs-window-item>

          <v-tabs-window-item value="general_settings">
            <general-settings-tab />
          </v-tabs-window-item>

          <v-tabs-window-item value="my_subscriptions">
            <user-subscriptions />
          </v-tabs-window-item>

          <v-tabs-window-item value="change_password">
            <update-password-form />
          </v-tabs-window-item>
        </v-tabs-window>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.settings-tabs {
  :deep(.v-tab) {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }

  &:deep(.v-tab--vertical) {
    justify-content: flex-start;
    text-align: start;
  }
}
</style>
